"use client";
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ModalContextType {
  isOpen: boolean;
  modalTitle: string;
  openModal: (title?: string) => void;
  closeModal: () => void;
  toggleModal: () => void;
  setModalTitle: (title: string) => void;
  isThankYouModalOpen: boolean;
  openThankYouModal: () => void;
  closeThankYouModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [isThankYouModalOpen, setIsThankYouModalOpen] = useState(false);

  const openModal = useCallback((title?: string) => {
    if (title) {
      setModalTitle(title);
    }
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleModal = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const openThankYouModal = useCallback(() => {
    setIsThankYouModalOpen(true);
  }, []);

  const closeThankYouModal = useCallback(() => {
    setIsThankYouModalOpen(false);
  }, []);

  const value: ModalContextType = {
    isOpen,
    modalTitle,
    openModal,
    closeModal,
    toggleModal,
    setModalTitle,
    isThankYouModalOpen,
    openThankYouModal,
    closeThankYouModal,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};
